// src/api/taskAi.ts
//
// AI-assisted task-creation helpers. These call the Teamlio backend (the `api`
// client's baseURL), which hosts the task_ai router at /tasks/ai. Used by the
// "Generate with AI" buttons on the task create / edit forms. Responses follow
// an `{ ok, ... }` contract: `ok: false` with a `message` means AI is
// unavailable / not configured, and the UI shows the message rather than
// treating it as a hard error. When a usage limit is hit (our per-user limiter
// or the provider's quota) the backend sends `code: "rate_limited"`; we surface
// a single canonical "upgrade" prompt for that case.
//
// `code: "credits_exhausted"` is a different case and its message is passed
// through UNCHANGED: the server already names which allowance ran out — this
// person's month, today's burst ceiling, or the whole workspace's pool — and
// therefore who can raise it. Substituting "daily AI limit … upgrade your plan"
// there would be wrong on all three counts.
import api from './api';

/** Shown when an AI usage limit is hit (our limiter or the provider's quota). */
export const AI_LIMIT_MESSAGE =
  "You've hit your daily AI limit. Upgrade your plan for more usage.";

/** Backend signals a usage limit with this `code` (HTTP 200, ok:false). */
const RATE_LIMITED_CODE = 'rate_limited';

/** Out of AI credits (HTTP 200, ok:false). Its `message` is authoritative. */
export const CREDITS_EXHAUSTED_CODE = 'credits_exhausted';

interface TaskAiBase {
  ok: boolean;
  /** Machine-readable cause when ok is false: "rate_limited" | "credits_exhausted". */
  code?: string;
  source?: string;
  message?: string;
}

export interface TaskAiDescriptionResponse extends TaskAiBase {
  description?: string;
}

export interface TaskAiChecklistResponse extends TaskAiBase {
  items?: string[];
}

// When the backend signals a usage limit, surface the canonical upgrade copy
// regardless of the server's message text so the prompt stays consistent.
function normalize<T extends TaskAiBase>(data: T): T {
  if (data && data.ok === false && data.code === RATE_LIMITED_CODE) {
    return { ...data, message: AI_LIMIT_MESSAGE };
  }
  return data;
}

// Defense-in-depth: an older backend may rate-limit with HTTP 429 instead of a
// 200 body. Convert that into the same graceful { ok:false, code:"rate_limited" }
// shape so callers show the upgrade prompt; re-throw anything else.
function handleLimitError<T extends TaskAiBase>(e: unknown): T {
  const status = (e as { response?: { status?: number } })?.response?.status;
  if (status === 429) {
    return { ok: false, code: RATE_LIMITED_CODE, message: AI_LIMIT_MESSAGE } as T;
  }
  throw e;
}

export const generateTaskDescription = (
  title: string,
  taskType?: string,
): Promise<TaskAiDescriptionResponse> =>
  api
    .post<TaskAiDescriptionResponse>('/tasks/ai/description', {
      title,
      task_type: taskType,
    })
    .then((r) => normalize(r.data))
    .catch((e): TaskAiDescriptionResponse => handleLimitError(e));

export const generateTaskChecklist = (
  title: string,
  description: string,
): Promise<TaskAiChecklistResponse> =>
  api
    .post<TaskAiChecklistResponse>('/tasks/ai/checklist', {
      title,
      description,
    })
    .then((r) => normalize(r.data))
    .catch((e): TaskAiChecklistResponse => handleLimitError(e));
